import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Chip,
  CircularProgress,
  Button,
  Breadcrumbs,
} from '@mui/material';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';
import { ArrowBack, CalendarToday, Person } from '@mui/icons-material';
import { fetchBlogPost, BlogPost } from '../../services/wordpress';
import { havenColors } from '../../theme/theme';

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (slug) {
      loadPost();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const loadPost = async () => {
    if (!slug) return;
    
    setLoading(true);
    try {
      const blogPost = await fetchBlogPost(slug);
      if (blogPost) {
        setPost(blogPost);
        setNotFound(false);
      } else {
        setNotFound(true);
      }
    } catch (error) {
      console.error('Error loading post:', error);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getAuthorName = (post: BlogPost): string => {
    return post._embedded?.author?.[0]?.name || 'HAVEN Team';
  };

  const getFeaturedImage = (post: BlogPost): string | null => {
    return post._embedded?.['wp:featuredmedia']?.[0]?.source_url || null;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh' }}>
        <CircularProgress size={50} sx={{ color: havenColors.primaryTeal }} />
      </Box>
    );
  }

  if (notFound || !post) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ mb: 4, color: havenColors.darkGray }}>
          Blog Post Not Found
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, color: '#666' }}>
          The blog post you're looking for doesn't exist or has been moved.
        </Typography>
        <Button
          component={RouterLink}
          to="/blog"
          variant="outlined"
          startIcon={<ArrowBack />}
          sx={{
            borderColor: havenColors.primaryTeal,
            color: havenColors.primaryTeal,
            '&:hover': {
              borderColor: havenColors.primaryTeal,
              backgroundColor: havenColors.primaryTeal,
              color: 'white',
            },
          }}
        >
          Back to Blog
        </Button>
      </Container>
    );
  }

  return (
    <Box sx={{ py: 4, backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <Container maxWidth="md">
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 4 }}>
          <Typography 
            component={RouterLink} 
            to="/" 
            sx={{ 
              color: havenColors.primaryTeal, 
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            Home
          </Typography>
          <Typography 
            component={RouterLink} 
            to="/blog" 
            sx={{ 
              color: havenColors.primaryTeal, 
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            Blog
          </Typography>
          <Typography color="text.primary">
            {post.title.rendered}
          </Typography>
        </Breadcrumbs>

        {/* Back Button */}
        <Button
          onClick={() => navigate('/blog')}
          startIcon={<ArrowBack />}
          sx={{
            mb: 4,
            color: havenColors.primaryTeal,
            '&:hover': {
              backgroundColor: `${havenColors.primaryTeal}10`
            }
          }}
        >
          Back to Blog
        </Button>

        {/* Article */}
        <Box sx={{ backgroundColor: 'white', borderRadius: 2, overflow: 'hidden', boxShadow: 2 }}>
          {/* Featured Image */}
          {getFeaturedImage(post) && (
            <Box
              component="img"
              src={getFeaturedImage(post)!}
              alt={post.title.rendered}
              sx={{
                width: '100%',
                height: { xs: '250px', md: '400px' },
                objectFit: 'cover'
              }}
            />
          )}

          {/* Content */}
          <Box sx={{ p: { xs: 3, md: 6 } }}>
            {/* Meta Info */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}>
              <Chip
                icon={<CalendarToday />}
                label={formatDate(post.date)}
                size="small"
                sx={{
                  backgroundColor: havenColors.primaryTeal,
                  color: 'white',
                  '& .MuiChip-icon': { color: 'white' }
                }}
              />
              <Chip
                icon={<Person />}
                label={`By ${getAuthorName(post)}`}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: havenColors.primaryTeal,
                  color: havenColors.primaryTeal
                }}
              />
            </Box>

            {/* Title */}
            <Typography variant="h3" sx={{
              fontWeight: 'bold',
              color: havenColors.darkGray,
              mb: 4,
              fontSize: { xs: '2rem', md: '2.5rem' },
              lineHeight: 1.2
            }}>
              {post.title.rendered}
            </Typography>

            {/* Content */}
            <Box
              sx={{
                '& p': {
                  marginBottom: 2,
                  lineHeight: 1.7,
                  fontSize: '1.1rem',
                  color: '#333'
                },
                '& h1, & h2, & h3, & h4, & h5, & h6': {
                  fontWeight: 'bold',
                  color: havenColors.darkGray,
                  marginTop: 3,
                  marginBottom: 2
                },
                '& h2': {
                  fontSize: '1.75rem'
                },
                '& h3': {
                  fontSize: '1.5rem'
                },
                '& ul, & ol': {
                  paddingLeft: 3,
                  marginBottom: 2
                },
                '& li': {
                  marginBottom: 1,
                  lineHeight: 1.6
                },
                '& blockquote': {
                  borderLeft: `4px solid ${havenColors.primaryTeal}`,
                  paddingLeft: 3,
                  marginLeft: 0,
                  marginRight: 0,
                  marginBottom: 2,
                  fontStyle: 'italic',
                  backgroundColor: '#f8f9fa',
                  padding: 2,
                  borderRadius: 1
                },
                '& img': {
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: 1,
                  marginBottom: 2
                },
                '& a': {
                  color: havenColors.primaryTeal,
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                },
                '& code': {
                  backgroundColor: '#f1f3f4',
                  padding: '2px 6px',
                  borderRadius: 1,
                  fontSize: '0.9em',
                  fontFamily: 'monospace'
                },
                '& pre': {
                  backgroundColor: '#f1f3f4',
                  padding: 2,
                  borderRadius: 1,
                  overflow: 'auto',
                  marginBottom: 2
                }
              }}
              dangerouslySetInnerHTML={{ __html: post.content.rendered }}
            />
          </Box>
        </Box>

        {/* Navigation */}
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Button
            component={RouterLink}
            to="/blog"
            variant="outlined"
            size="large"
            sx={{
              borderColor: havenColors.primaryTeal,
              color: havenColors.primaryTeal,
              fontWeight: 'bold',
              px: 4,
              py: 1.5,
              '&:hover': {
                borderColor: havenColors.primaryTeal,
                backgroundColor: havenColors.primaryTeal,
                color: 'white',
              },
            }}
          >
            Read More Articles
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default BlogPostPage; 